import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import getSourceDataAuditLogs from "@app/services/cases/getSourceDataAuditLogs";
import updateSourceDataById from "@app/services/cases/updateSourceDataById";

import useAuthorization from "@hooks/useAuthorization";
import { useToast } from "@hooks/useToast";

import { AdminPermissions } from "@src/enum/Permissions";

import { CASES_DATA_STATUS_TYPE } from "@utils/enum";

import { useDataSourceDetailsContext } from "../../providers";
import { getAllDocumentsConfig } from "./DetailsTab.utils";

export const useDetailsTabInformation = () => {
  const { dataSourceDetails } = useDataSourceDetailsContext();
  const { formatMessage, messages } = useIntl();
  const { showErrorToast } = useToast();

  const [activityLogData, setActivityLogData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const dataSourceType = useMemo(() => {
    const translationKey = `datasource_${dataSourceDetails?.type}`;
    const value = formatMessage({ id: translationKey });

    return value ?? "";
  }, [dataSourceDetails?.type, formatMessage]);

  const changeStatusButtonLabel = useMemo(() => {
    const shouldChangeToCompleted =
      dataSourceDetails?.status === CASES_DATA_STATUS_TYPE.MANUAL ||
      dataSourceDetails?.status === CASES_DATA_STATUS_TYPE.PENDING;
    const translationKey = shouldChangeToCompleted
      ? "label_change_to_completed"
      : "label_change_to_manual";
    const value = formatMessage({ id: translationKey });

    return value ?? "";
  }, [dataSourceDetails?.status, formatMessage]);

  const cardDetails = useMemo(() => {
    const statusTranslationsByValue = {
      [CASES_DATA_STATUS_TYPE.PENDING]: messages.label_cases_status_pending,
      [CASES_DATA_STATUS_TYPE.MANUAL]: messages.label_manual,
      [CASES_DATA_STATUS_TYPE.COMPLETED]:
        messages[CASES_DATA_STATUS_TYPE.COMPLETED],
    };

    return [
      {
        labelKey: "title_id",
        value: dataSourceDetails?.id,
      },
      {
        labelKey: "label_case_id",
        value: dataSourceDetails?.case_id,
      },
      {
        labelKey: "label_status",
        value: statusTranslationsByValue[dataSourceDetails?.status],
      },
    ];
  }, [
    dataSourceDetails?.case_id,
    dataSourceDetails?.id,
    dataSourceDetails?.status,
    messages,
  ]);

  const reasonsForManual = useMemo(
    () => dataSourceDetails?.data?.failed ?? [],
    [dataSourceDetails?.data?.failed]
  );

  const allDocumentsConfig = useMemo(
    () =>
      dataSourceDetails?.data?.hits
        ? getAllDocumentsConfig(dataSourceDetails?.data?.hits)
        : null,
    [dataSourceDetails?.data?.hits]
  );

  useEffect(() => {
    if (!dataSourceDetails?.id) return;

    const fetchActivityLogData = async () => {
      setIsLoading(true);

      try {
        const {
          data: [sourceData],
        } = await getSourceDataAuditLogs(dataSourceDetails?.id);

        const { audit_logs } = sourceData;

        setActivityLogData(audit_logs);
      } catch {
        showErrorToast(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchActivityLogData();
  }, [dataSourceDetails?.id, messages.exception_error_message, showErrorToast]);

  return {
    dataSourceType,
    changeStatusButtonLabel,
    cardDetails,
    reasonsForManual,
    allDocumentsConfig,
    activityLogData,
    isLoading,
  };
};

export const useDetailsTabActions = () => {
  const { hasAllPermissions } = useAuthorization();
  const { dataSourceDetails, setDataSourceDetails } =
    useDataSourceDetailsContext();
  const { messages } = useIntl();
  const { showErrorToast, showSuccessToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);

  const hasWriteDataSourcesPermission = hasAllPermissions([
    AdminPermissions.AdminWriteDataSources,
  ]);

  const handleUpdateStatus = async () => {
    const { id, status, case_id } = dataSourceDetails;

    try {
      setIsLoading(true);

      const targetStatus =
        status === CASES_DATA_STATUS_TYPE.MANUAL ||
        status === CASES_DATA_STATUS_TYPE.PENDING
          ? CASES_DATA_STATUS_TYPE.COMPLETED
          : CASES_DATA_STATUS_TYPE.MANUAL;
      const payload = {
        status: targetStatus,
      };

      await updateSourceDataById(case_id, id, payload);

      setDataSourceDetails(prevValue => ({
        ...prevValue,
        status: targetStatus,
      }));

      const successMessage =
        targetStatus === CASES_DATA_STATUS_TYPE.MANUAL ||
        targetStatus === CASES_DATA_STATUS_TYPE.IN_PROGRESS
          ? messages.label_changed_to_manual
          : messages.label_changed_to_completed;

      showSuccessToast(successMessage);
    } catch {
      showErrorToast(messages.exception_error_message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    hasWriteDataSourcesPermission,
    isLoading,
    handleUpdateStatus,
  };
};
